/**
 * Profile Detail — effects.
 *
 * - LoadDetail: GET /api/v1/{friend|sponsor}/discovery/profiles/{id}
 * - OpenPersonalChat: POST /api/v1/chat/channels/personal (the canonical
 *   chat seam, Bucket C symmetric). On success, the harness navigates
 *   to /chat/:channelId.
 *
 * R1+R2 (correction ACT): runLoadDetail now passes through the
 * owner's `gender` and `goal` fields so the public role label can
 * be rendered from owner fields.
 *
 * R8 (correction ACT): navigation callback is supplied by the
 * EffectContext (harness), not the effect payload. The effect is
 * pure data; the side-effect closure is environment.
 */

import {
  getFriendProfileById,
  getSponsorProfileById,
} from '@/api/profiles';
import { openPersonalChat } from '@/api/chat';
import { normalizeProfilePhotosField } from '@/api/profilePhotos';
import type { ProfileDetailEffect } from './update';
import type { ProfileDetailMsg } from './messages';

export interface EffectContext {
  readonly dispatch: (msg: ProfileDetailMsg) => void;
  /**
   * R8 (correction ACT): the navigation callback lives here, in
   * the harness-supplied environment, not in the effect payload.
   * The pure reducer never closes over navigation.
   *
   * Bucket C: `registrationKind` is REMOVED. The CTA is symmetric;
   * the server is authoritative for both directions. The harness
   * no longer needs to know the viewer's role.
   */
  readonly navigateToChannelId: (channelId: string) => void;
}

export async function runEffect(
  effect: ProfileDetailEffect,
  context: EffectContext
): Promise<void> {
  switch (effect.type) {
    case 'LoadDetail':
      return runLoadDetail(effect.profileId, effect.variant, context);
    case 'OpenPersonalChat':
      return runOpenPersonalChat(effect.targetProfileId, context);
    default: {
      const _exhaustive: never = effect;
      return;
    }
  }
}

async function runLoadDetail(
  profileId: string,
  variant: 'friend' | 'sponsor',
  context: EffectContext
): Promise<void> {
  void context.navigateToChannelId;
  try {
    // The variant determines which endpoint to call:
    //   friend detail (sponsor viewing a friend) -> /sponsor/discovery/profiles/{id}
    //   sponsor detail (friend viewing a sponsor) -> /friend/discovery/profiles/{id}
    const response =
      variant === 'friend'
        ? await getFriendProfileById(profileId)
        : await getSponsorProfileById(profileId);
    // CORRECTION03 (B-2.B): normalize the photos field through the
    // single-source-of-truth normalizer in `@/api/profilePhotos`. The
    // wire may carry `photos`, `null`, an empty array, an unknown
    // shape, or omit the field entirely; all five are canonicalized
    // here. We MUST NOT reconstruct role-prefixed URLs locally.
    const { photos, violations: photoProtocolViolations } =
      normalizeProfilePhotosField(response.photos);
    context.dispatch({
      type: 'DetailLoaded',
      detail: {
        id: response.id,
        profile_type: response.profile_type,
        // R1+R2: pass through owner gender/goal for label rendering.
        gender: response.gender,
        goal: response.goal,
        display_name: response.display_name,
        headline: response.headline,
        bio: response.bio,
        city_or_region: response.city_or_region,
        updated_at: response.updated_at,
        photos,
      },
      photoProtocolViolations,
    });
  } catch (err) {
    const errorMessage =
      err instanceof Error ? err.message : 'Не удалось загрузить анкету';
    context.dispatch({ type: 'DetailLoadFailed', error: errorMessage });
  }
}

async function runOpenPersonalChat(
  targetProfileId: string,
  context: EffectContext
): Promise<void> {
  try {
    // Bucket C: openPersonalChat takes target_profile_id (not
    // friend_profile_id) and is symmetric — both Sponsor-viewing-Friend
    // and Friend-viewing-Sponsor now work.
    const { channel } = await openPersonalChat(targetProfileId);
    context.dispatch({
      type: 'MessageCtaSucceeded',
      channelId: channel.id,
    });
    context.navigateToChannelId(channel.id);
  } catch (err) {
    let errorMessage = 'Не удалось открыть чат';
    if (err instanceof Error) {
      if (err.message === 'authentication_required') {
        errorMessage = 'Войдите, чтобы открыть чат';
      } else if (err.message === 'not_authorized') {
        // Bucket C: the rejection no longer mentions Intro. Keep
        // the user-facing copy generic; the server is authoritative
        // and returns a non-leaking reason.
        errorMessage = 'Чат с этим пользователем сейчас недоступен.';
      } else if (err.message === 'target_profile_not_found') {
        errorMessage = 'Анкета не найдена';
      } else if (err.message === 'service_unavailable') {
        errorMessage = 'Чат временно недоступен';
      }
    }
    context.dispatch({
      type: 'MessageCtaFailed',
      error: errorMessage,
    });
  }
}